/**
 * Single Note View - Displays a single note in a focused view
 * Used when clicking "View Note" links in AI answers
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReadingNote } from '../types/reading-notes';
import { API_BASE_URL } from '../config/api';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import { S3Image } from '../components/S3Image';
import {
  ArrowLeftIcon,
  BookOpenIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';

export default function SingleNoteView() {
  const { bookId, noteId } = useParams<{ bookId?: string; noteId: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();

  const [note, setNote] = useState<ReadingNote | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get return page from sessionStorage (from Dhyana)
  const returnToPage = React.useMemo(() => {
    const stored = sessionStorage.getItem('returnToPage');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  // Get return to chat from sessionStorage (from GeneralChat)
  const returnToChat = React.useMemo(() => {
    const stored = sessionStorage.getItem('returnToChat');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  // Fetch note on mount
  useEffect(() => {
    const fetchNote = async () => {
      if (!noteId) {
        setError('Invalid note URL');
        setIsLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('access_token');
        // Use the appropriate endpoint based on whether bookId is provided
        const url = bookId 
          ? `${API_BASE_URL}/api/books/${bookId}/notes/${noteId}`
          : `${API_BASE_URL}/api/notes/${noteId}`;
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const noteData = await response.json();
          setNote(noteData);
        } else if (response.status === 404) {
          setError('Note not found');
        } else {
          setError('Failed to load note');
        }
      } catch (err) {
        console.error('Error fetching note:', err);
        setError('Failed to load note');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNote();
  }, [bookId, noteId]);

  // Handle return - could be to Dhyana or to Chat
  const handleReturn = () => {
    // Check if we came from chat
    if (returnToChat) {
      sessionStorage.removeItem('returnToChat');
      if (returnToChat.sessionId) {
        navigate(`/chat?session=${returnToChat.sessionId}`);
      } else {
        navigate('/chat');
      }
      return;
    }
    
    // Otherwise, return to Dhyana
    if (returnToPage && returnToPage.bookId && returnToPage.pageNumber) {
      navigate(`/dhyana/${returnToPage.bookId}?page=${returnToPage.pageNumber}`);
      sessionStorage.removeItem('returnToPage');
    } else if (bookId) {
      navigate(`/dhyana/${bookId}`);
    } else if (note?.book_id) {
      navigate(`/dhyana/${note.book_id}`);
    } else {
      navigate('/library');
    }
  };

  // Handle jump to page where note was created
  // A note pinned from a video → metadata carries video_id + timestamp_sec.
  const videoNoteId = note?.note_metadata?.video_id as string | undefined;
  const videoNoteSec = note?.note_metadata?.timestamp_sec as number | undefined;
  const isVideoNote = note?.note_metadata?.source === 'video' && !!videoNoteId && typeof videoNoteSec === 'number';
  const fmtNoteTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  // Inline [m:ss] seek-pills in a video note's content → open that moment.
  const onNoteTimestampClick = videoNoteId
    ? (sec: number) => navigate(`/watch/${videoNoteId}?t=${sec}`, { state: { fromNote: true } })
    : undefined;

  const handleJumpToNotePage = () => {
    // Video note → the lecture at the noted moment, with "Back to note" on return.
    if (isVideoNote) {
      navigate(`/watch/${videoNoteId}?t=${videoNoteSec}`, { state: { fromNote: true } });
      return;
    }
    const targetBookId = bookId || note?.book_id;
    if (note && targetBookId) {
      navigate(`/dhyana/${targetBookId}?page=${note.page_number}`);
      sessionStorage.removeItem('returnToPage');
      sessionStorage.removeItem('returnToChat');
    }
  };

  // Handle copy to clipboard
  const handleCopy = async () => {
    if (!note) return;
    
    const text = [
      note.content_text,
      note.user_note
    ].filter(Boolean).join('\n\n');

    try {
      await navigator.clipboard.writeText(text);
      showToast('success', 'Copied to clipboard');
    } catch (err) {
      showToast('error', 'Failed to copy');
    }
  };

  // Get note type label
  const getNoteTypeLabel = () => {
    if (!note) return '';
    switch (note.note_type) {
      case 'highlight':
        return 'Highlight';
      case 'thought':
        return 'Thought';
      case 'chat_answer':
        return 'AI Answer';
      default:
        return 'Note';
    }
  };

  // Get indexing status icon
  const getIndexingStatusIcon = () => {
    if (!note) return null;
    if (note.is_indexed) {
      return <CheckCircleIcon className="w-5 h-5 text-emerald-500" title="Indexed" />;
    }
    if (note.indexing_status === 'running' || note.indexing_status === 'pending') {
      return <ClockIcon className="w-5 h-5 text-amber-500 animate-pulse" title="Indexing..." />;
    }
    if (note.indexing_status === 'failed') {
      return <ExclamationCircleIcon className="w-5 h-5 text-red-500" title="Indexing failed" />;
    }
    return null;
  };

  // Get note type badge color
  const getNoteTypeBadgeClass = () => {
    if (!note) return '';
    switch (note.note_type) {
      case 'highlight':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
      case 'thought':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
      case 'chat_answer':
        return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300';
      default:
        return 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300';
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#030712]' : 'bg-[#FAFBFC]'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-10 border-b ${
        isDarkMode 
          ? 'bg-slate-900/95 border-slate-700/50 backdrop-blur-xl' 
          : 'bg-white/95 border-gray-200 backdrop-blur-xl'
      }`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleReturn}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                isDarkMode
                  ? 'text-white hover:bg-slate-800'
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>
                {returnToChat 
                  ? 'Return to Chat' 
                  : returnToPage 
                    ? `Return to Page ${returnToPage.pageNumber}` 
                    : 'Back to Book'}
              </span>
            </button>

            <div className="flex items-center gap-2">
              {note && (
                <>
                  <button
                    onClick={handleJumpToNotePage}
                    className={`p-2 rounded-lg transition-colors ${
                      isVideoNote
                        ? 'text-cyan-600 dark:text-[#06B6D4] hover:bg-cyan-100 dark:hover:bg-[#06B6D4]/10'
                        : isDarkMode
                        ? 'hover:bg-slate-800 text-slate-400'
                        : 'hover:bg-gray-100 text-gray-500'
                    }`}
                    title={isVideoNote ? `Watch at ${fmtNoteTime(videoNoteSec as number)}` : `Jump to Page ${note.page_number}`}
                  >
                    {isVideoNote ? <PlayCircleIcon className="h-5 w-5" /> : <BookOpenIcon className="h-5 w-5" />}
                  </button>
                  <button
                    onClick={handleCopy}
                    className={`p-2 rounded-lg transition-colors ${
                      isDarkMode 
                        ? 'hover:bg-slate-800 text-slate-400' 
                        : 'hover:bg-gray-100 text-gray-500'
                    }`}
                    title="Copy to clipboard"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-3 border-cyan-500 border-t-transparent"></div>
            <p className={`mt-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              Loading note...
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-24">
            <ExclamationCircleIcon className={`h-16 w-16 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
            <h2 className={`mt-4 text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {error}
            </h2>
            <p className={`mt-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              The note you're looking for could not be found.
            </p>
            <button
              onClick={handleReturn}
              className="mt-6 px-6 py-2.5 rounded-xl text-white font-medium"
              style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
            >
              Go Back
            </button>
          </div>
        ) : note ? (
          <div className={`rounded-2xl border overflow-hidden ${
            isDarkMode 
              ? 'bg-slate-900/50 border-slate-700/50' 
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            {/* Note Header */}
            <div className={`px-6 py-4 border-b ${
              isDarkMode ? 'border-slate-700/50' : 'border-gray-100'
            }`}>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getNoteTypeBadgeClass()}`}>
                  {getNoteTypeLabel()}
                </span>
                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  Page {note.page_number}
                </span>
                {getIndexingStatusIcon()}
              </div>
            </div>

            {/* Note Content */}
            <div className="p-6 space-y-6">
              {/* Main Content */}
              {note.note_type === 'chat_answer' && note.normalized_content ? (
                <div className={`prose max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
                  <MarkdownRenderer content={note.normalized_content} onTimestampClick={onNoteTimestampClick} />
                </div>
              ) : note.content_text ? (
                <div className={`prose max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
                  <MarkdownRenderer content={note.content_text} onTimestampClick={onNoteTimestampClick} />
                </div>
              ) : null}

              {/* Images */}
              {note.image_urls && note.image_urls.length > 0 && (
                <div>
                  <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                    {note.image_urls.length} Attached Image{note.image_urls.length > 1 ? 's' : ''}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {note.image_urls.map((url, index) => (
                      <div key={index} className="aspect-square rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-700">
                        <S3Image 
                          src={url} 
                          alt={`Attachment ${index + 1}`} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Attachment Context - skip for chat_answer notes since it's embedded in normalized_content */}
              {note.attachment_context && note.note_type !== 'chat_answer' && (
                <div className={`border-l-4 pl-4 py-3 rounded-r ${
                  isDarkMode 
                    ? 'border-cyan-500/50 bg-cyan-900/20' 
                    : 'border-cyan-500/50 bg-cyan-50'
                }`}>
                  <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                    Question Context
                  </div>
                  <div className={`text-sm whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                    {note.attachment_context}
                  </div>
                </div>
              )}

              {/* User Note */}
              {note.user_note && (
                <div className={`border-l-4 pl-4 py-3 rounded-r ${
                  isDarkMode 
                    ? 'border-slate-600 bg-slate-800/50' 
                    : 'border-gray-300 bg-gray-50'
                }`}>
                  <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    Your Note
                  </div>
                  <div className={`text-sm whitespace-pre-wrap ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                    {note.user_note}
                  </div>
                </div>
              )}

              {/* Concept Tags */}
              {((note.concept_tags && note.concept_tags.length > 0) || 
                (note.extracted_concept_tags && note.extracted_concept_tags.length > 0)) && (
                <div>
                  <h3 className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    Concepts
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {note.concept_tags?.map((tag, index) => (
                      <span
                        key={`user-${index}`}
                        className={`px-3 py-1 rounded-full text-sm ${
                          isDarkMode 
                            ? 'bg-slate-800 text-slate-300 border border-slate-700' 
                            : 'bg-gray-100 text-gray-700 border border-gray-200'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                    {note.extracted_concept_tags?.map((tag, index) => (
                      <span
                        key={`ai-${index}`}
                        className={`px-3 py-1 rounded-full text-sm italic ${
                          isDarkMode 
                            ? 'bg-slate-800/50 text-slate-400 border border-slate-700/50' 
                            : 'bg-gray-50 text-gray-500 border border-gray-200'
                        }`}
                        title="AI-extracted concept"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`px-6 py-4 border-t ${
              isDarkMode ? 'border-slate-700/50 bg-slate-800/30' : 'border-gray-100 bg-gray-50'
            }`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                  Created {new Date(note.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                <button
                  onClick={handleJumpToNotePage}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white transition-all"
                  style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
                >
                  {isVideoNote ? (
                    <><PlayCircleIcon className="h-4 w-4" /> Watch at {fmtNoteTime(videoNoteSec as number)}</>
                  ) : (
                    <><BookOpenIcon className="h-4 w-4" /> Open in Book (Page {note.page_number})</>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}

